import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Paper,
} from '@mui/material';
import {
  People,
  Folder,
  Person,
  AdminPanelSettings,
  Description,
  PersonOff,
  Warning,
  CarCrash,
  HealthAndSafety,
  LocalShipping,
  Warehouse,
  PersonSearch,
  PersonAdd,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import NewsDashboardWidget from '../components/NewsDashboardWidget';
import VehicleInspectionsDashboardWidget from '../components/VehicleInspectionsDashboardWidget';

const Dashboard: React.FC = () => {
  const { user, isManager } = useAuth();

  const cards = [
    {
      title: 'Documents',
      description: 'Access and manage HR documents',
      icon: <Folder sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/documents', '_blank'),
      show: true,
    },
    {
      title: 'User Management',
      description: 'Manage company users and roles',
      icon: <People sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/users', '_blank'),
      show: isManager,
    },
    {
      title: 'Candidates',
      description: 'Manage job candidates and interviews',
      icon: <PersonSearch sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      action: () => window.open('/candidates', '_blank'),
      show: isManager,
    },
    {
      title: 'New Hires',
      description: 'Track new hire onboarding progress',
      icon: <PersonAdd sx={{ fontSize: 40, color: '#388e3c' }} />,
      action: () => window.open('/new-hires', '_blank'),
      show: isManager,
    },
    {
      title: 'My Profile',
      description: 'Update your personal information',
      icon: <Person sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/profile', '_blank'),
      show: true,
    },
    {
      title: 'Probationary Evaluation',
      description: 'Complete probationary evaluation forms',
      icon: <Description sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/probationary-evaluation-form.html', '_blank'),
      show: true,
    },
    {
      title: 'Corrective Action',
      description: 'Document corrective action procedures',
      icon: <Warning sx={{ fontSize: 40, color: '#f57c00' }} />,
      action: () => window.open('/corrective-action-form.html', '_blank'),
      show: true,
    },
    {
      title: 'Employee Termination',
      description: 'Process employee termination forms',
      icon: <PersonOff sx={{ fontSize: 40, color: '#d32f2f' }} />,
      action: () => window.open('/employee-termination-form.html', '_blank'),
      show: true,
    },
    {
      title: 'Accident Crash Report',
      description: 'Document vehicle accidents and incidents',
      icon: <CarCrash sx={{ fontSize: 40, color: '#d32f2f' }} />,
      action: () => window.open('/accident-crash-report-form.html', '_blank'),
      show: true,
    },
    {
      title: 'Incident/Injury Investigation',
      description: 'Document workplace incidents and injuries',
      icon: <HealthAndSafety sx={{ fontSize: 40, color: '#f57c00' }} />,
      action: () => window.open('/incident-injury-investigation-form.html', '_blank'),
      show: true,
    },
    {
      title: 'Vehicle Inspections',
      description: 'Inspect trucks and trailers for compliance',
      icon: <LocalShipping sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/vehicle-docs', '_blank'),
      show: true,
    },
    {
      title: 'Warehouse Inspection',
      description: 'Monthly warehouse safety inspection',
      icon: <Warehouse sx={{ fontSize: 40, color: '#1976d2' }} />,
      action: () => window.open('/warehouse-inspection-form.html', '_blank'),
      show: true,
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome, {user?.first_name}!
        </Typography>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Midwest Automotive Trucking Internal Portal
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Chip
            icon={isManager ? <AdminPanelSettings /> : <Person />}
            label={isManager ? 'Manager' : 'Employee'}
            color={isManager ? 'primary' : 'default'}
          />
          {user?.department && (
            <Chip label={user.department} variant="outlined" />
          )}
          {user?.employee_id && (
            <Chip label={`ID: ${user.employee_id}`} variant="outlined" />
          )}
        </Box>
      </Box>

      {/* News Widget - At the top */}
      <Paper sx={{ mb: 4, p: 3 }}>
        <NewsDashboardWidget />
      </Paper>

      <Grid container spacing={3}>
        {cards
          .filter(card => card.show)
          .map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 3,
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {card.icon}
                  </Box>
                  <Typography gutterBottom variant="h5" component="h2">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {card.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    size="large"
                    variant="contained"
                    onClick={card.action}
                    fullWidth
                    sx={{ mx: 2 }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Vehicle Inspections Widget */}
      <Paper sx={{ mt: 4, p: 3 }}>
        <VehicleInspectionsDashboardWidget />
      </Paper>

      {isManager && (
        <Box sx={{ mt: 4, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Manager Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.open('/users', '_blank')}
                startIcon={<People />}
              >
                Manage Users
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.open('/documents', '_blank')}
                startIcon={<Folder />}
              >
                Manage Documents
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 4, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary" align="center">
          Last login: {user?.last_login ? new Date(user.last_login).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short', hour12: true, timeZone: 'America/New_York' }) : 'Never'}
        </Typography>
      </Box>
    </Container>
  );
};

export default Dashboard;